/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Backdrop,
  Button,
  CircularProgress,
  InputAdornment,
  Snackbar,
  TextField,
  Container,
  Grid2,
} from "@mui/material";
import "./ApplicationTeamsInfo.css";
import AddIcon from "@mui/icons-material/Add";
import PopupDialog from "../PopupDialog";
import EditIcon from "@mui/icons-material/Edit";
import APIManager from "../../Services/ApiManager/ApiManager";
import TeamTableSection from "./TeamInfoTableSection";
import { jwtDecode } from "jwt-decode";
import { useDispatch, useSelector } from "react-redux";
import { fetchTeamDetails } from "../../Services/redux/team-details/TeamDetailsActions";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Topheader from "../../Layout/header/header";
import ApplicationDetailsSideMenu from "../ApplicationDetailsSideMenu/ApplicationDetailsSideMenu";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { fetchApplicationDetails } from "../../Services/redux/appId-details/AppIdDeatilsActions";
import { fetchSubmissionDetails } from "../../Services/redux/Application-details/ApplicationDetailsActions";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ApplicationDetailsErrorDialog from "../ApplicationDetailsErrorDialog/ApplicationDetailsErrorDialog";

const MAX_LENGTH = 15;
const MIN_LENGTH = 3;
function ApplicationTeamsInfo({ teamsData }) {
  const { appId } = useParams();
  const location = useLocation();
  const submissionId = location.state.submissionId;
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [componentName, setComponentName] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [popupData, setPopupData] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [loggedInUserRole, setLoggedInUserRole] = useState("");
  const loadTeamsData = useSelector((state) => state.teamDetails.teams);
  const [loader, setLoader] = useState(false);
  const [teamReadOnly, setTeamReadOnly] = useState(true);
  const [teamName, setTeamName] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const appData = useSelector(
    (state) => state.applicationDetails.applicationDetails
  );
  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails
  );
  const errors = useSelector((state) => state.errors);
  const username = jwtDecode(sessionStorage.getItem("id_token"))[
    "cognito:username"
  ];

  const handleAddTeamMember = () => {
    setPopupData({
      id: "",
      email: "",
      role: "MANAGER",
    });
    setOpenErrorPopup(true);
    setComponentName("teams");
    setHeaderMessage("Add Team Member");
  };

  const handleCloseDialog = () => {
    setOpenErrorPopup(false);
  };

  const handleAddEditTeam = (memberData) => {
    setOpenErrorPopup(false);
    setLoader(true);
    APIManager.putAddTeamMember(teamId, memberData)
      .then((json) => {
        const updatedTeamsData = json;
        dispatch(fetchTeamDetails(teamId));
        prepareTeamsData(updatedTeamsData);
        if (memberData.id === undefined) {
          setSnackMsg(`Member ${memberData.email} is added successfully`);
        } else {
          setSnackMsg(`Member ${memberData.email} is updated successfully`);
        }
        setOpenSnackbar(true);
        setLoader(false);
      })
      .catch((error) => {
        setComponentName("appError");
        setErrorMessage(error.message);
        setOpenErrorPopup(true);
        setLoader(false);
      });
  };

  const handleEditTeamName = () => {
    setTeamReadOnly(false);
  };

  const handleCloseTeamInput = () => {
    setTeamName(loadTeamsData.name);
    setTeamReadOnly(true);
  };

  const handleTeamName = (event) => {
    setTeamName(event.target.value);
  };

  const handleSubmitTeamName = () => {
    setLoader(true);
    let teamData = { name: teamName };
    APIManager.patchAddTeamMember(teamId, teamData)
      .then((json) => {
        const updatedTeamsData = json;
        dispatch(fetchTeamDetails(teamId));
        prepareTeamsData(updatedTeamsData);
        setTeamReadOnly(true);
        setSnackMsg(`Team Name ${loadTeamsData.name} is updated successfully`);
        setOpenSnackbar(true);
        setLoader(false);
      })
      .catch((error) => {
        setComponentName("appError");
        setErrorMessage(error.message);
        setOpenErrorPopup(true);
        setLoader(false);
      });
  };

  const handleDeleteTeam = (memberData) => {
    setOpenErrorPopup(false);

    setLoader(true);

    APIManager.deleteTeamMember(teamId, memberData)
      .then((json) => {
        const updatedTeamsData = json;
        dispatch(fetchTeamDetails(teamId));
        prepareTeamsData(updatedTeamsData);
        setLoader(false);
        setSnackMsg(`Member ${memberData.email} is deleted successfully`);
        setOpenSnackbar(true);
      })
      .catch((error) => {
        setComponentName("appError");
        setErrorMessage(error.message);
        setOpenErrorPopup(true);
        setLoader(false);
        setOpenSnackbar(false);
      });
  };

  const handleDeleteTeamPopup = (memberData) => {
    setHeaderMessage("Delete Team Member");
    setComponentName("deleteTeamMember");
    setErrorMessage(
      "Are you sure, you want to remove a member from this list?"
    );
    setPopupData(memberData);
    setOpenErrorPopup(true);
  };

  const handleEditTeamPopup = (memberData) => {
    setPopupData(memberData);
    setHeaderMessage("Edit Team Member");
    setComponentName("editTeamMember");
    setOpenErrorPopup(true);
  };

  const prepareTeamsData = (teamsData) => {
    setTeamId(teamsData.id);
    let managerList = [];
    let memberList = [];
    if (teamsData.managers !== undefined && teamsData.managers.length > 0) {
      managerList = teamsData.managers.map((manager) => {
        if (username === manager.id) {
          setLoggedInUserRole("MANAGER");
        }
        manager["role"] = "MANAGER";
        return manager;
      });
    }
    if (teamsData.members !== undefined && teamsData.members.length > 0) {
      memberList = teamsData.members.map((member) => {
        if (username === member.id) {
          setLoggedInUserRole("MEMBER");
        }
        member["role"] = "MEMBER";
        return member;
      });
    }
    const fullTeamsData = [...managerList, ...memberList];
    setTeams([...fullTeamsData]);
  };

  const backToApplications = () => {
    navigate(`/ui/developer/applications/${appId}`, {
      state: {
        teamId: sessionStorage.getItem("teamId"),
        submissionId: submissionId,
      },
    });
  };

  useEffect(() => {
    if (appId !== undefined) {
      dispatch(fetchApplicationDetails(appId));
      dispatch(fetchSubmissionDetails(appId, submissionId));
    }
  }, [appId]);

  useEffect(() => {
    setLoader(true);
    if (globalThis.location.toString().includes("teamsInfo")) {
      dispatch(fetchTeamDetails(sessionStorage.getItem("teamId")));
    }
    setLoader(false);
  }, []);
 
  useEffect(() => {
    setLoader(true);
    if (globalThis.location.toString().includes("teamsInfo")) {
      if (loadTeamsData?.name) {
        setTeamName(loadTeamsData.name);
        prepareTeamsData(loadTeamsData);
      }
    } else if (teamsData?.id) {
        setTeamId(teamsData.id);
        setTeamName(teamsData.name);
 
        let managerList = [];
        let memberList = [];
 
        if (teamsData.managers?.length > 0) {
          managerList = teamsData.managers.map((manager) => {
            if (username === manager.id) {
              setLoggedInUserRole("MANAGER");
            }
            return { ...manager, role: "MANAGER" };
          });
        }
 
        if (teamsData.members?.length > 0) {
          memberList = teamsData.members.map((member) => {
            if (username === member.id) {
              setLoggedInUserRole("MEMBER");
            }
            return { ...member, role: "MEMBER" };
          });
        }
 
        setTeams([...managerList, ...memberList]);
      }
    setLoader(false);
  }, [loadTeamsData, teamsData]);

  return (
    <>
      {loader && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Topheader />
      <Container
        maxWidth="lg"
        sx={{ width: "100% !important", maxWidth: "100% !important" }}
      >
        <Grid2 container>
          <Grid2 size={12}>
            <div className="container-my-release">
              <div
                className="side-menu-my-release"
                style={{ marginTop: "69px" }}
              >
                <button style={{ cursor: "pointer", background: "none", border: "none", padding: 0 }}
                  className="arrow-back " 
                  onClick={backToApplications}
                  type="button"
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <ArrowBackIcon style={{ color: " #717273" }} />
                  </div>
                  <div className="my-apps">Back To App Details</div>
                </button>
                {appData && Object.keys(appData).length > 0 && (
                  <ApplicationDetailsSideMenu
                    appData={appData}
                    teamId={teamId}
                  />
                )}
              </div>
              <div className="teams-info-section" id="team-info">
                <div
                  className="listing-header-wrapper-teams-info"
                  style={{
                    borderBottom: "1px solid  #12141533",
                    width: "85%",
                    marginTop: "21px",
                  }}
                >
                  {submissionData && Object.keys(submissionData).length > 0  && appData && Object.keys(appData).length > 0 &&(
                    <div className="app-icon-my-release">
                      <div className="icon-name-my-release">
                        <img
                          style={{ borderRadius: "4px" }}
                          src={submissionData.icon.url}
                          alt={submissionData.fileName}
                          width={60}
                          height={60}
                        />
                        {
                          <div className="name-status-team-info">
                            <p className="application-name">
                              {appData.name}
                            </p>
                          </div>
                        }
                      </div>
                    </div>
                  )}
                </div>

                <p>
                  <span className="teams-info-text"> Teams Info </span>
                  <span className="companyTextCls">
                    Company: {sessionStorage.getItem("companyName")}
                  </span>
                </p>

                <div className="teamDivCls">
                  {loggedInUserRole === "MANAGER" && (
                    <div>
                      {teamReadOnly && (
                        <TextField
                          sx={{
                            width: "300px !important",
                          }}
                          id="outlined-basic"
                          type="text"
                          variant="outlined"
                          value={teamName}
                          slotProps={{
                            input: {
                              readOnly: true,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <EditIcon
                                    className="editIconCls"
                                    onClick={handleEditTeamName}
                                  />
                                </InputAdornment>
                              ),
                            },
                          }}
                          required
                        />
                      )}

                      {!teamReadOnly && (
                        <TextField
                          sx={{
                            width: "300px !important",
                          }}
                          autoFocus
                          autoComplete="off"
                          required
                          id="outlined-basic"
                          type="text"
                          variant="outlined"
                          fullWidth
                          value={teamName}
                          helperText=""
                          onChange={handleTeamName}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <CheckIcon
                                    className="editIconCls"
                                    onClick={handleSubmitTeamName}
                                  />
                                  &nbsp;
                                  <CloseIcon
                                    className="editIconCls"
                                    onClick={handleCloseTeamInput}
                                  />
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                      )}
                    </div>
                  )}

                  {loggedInUserRole === "MEMBER" && (
                    <div>
                      <p className="team-title-text">{loadTeamsData.name}</p>
                    </div>
                  )}

                  {loggedInUserRole === "MANAGER" && (
                    <Button
                      style={{
                        background: "221.2 83.2% 53.3%",
                        textTransform: "none",
                        height: "32px",
                        maxWidth: "190px",
                        gap: "8px",
                      }}
                      variant="contained"
                      onClick={handleAddTeamMember}
                    >
                      <AddIcon sx={{ marginLeft: "-6px" }} /> Add Team Member
                    </Button>
                  )}
                </div>
                <div className="tables-div">
                  <table className="employee-table">
                    <thead>
                      <tr>
                        <th>First/Last Name</th>
                        <th>User ID (Email)</th>
                        <th>Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teams.map((employee) => (
                        <TeamTableSection
                          key={employee.id || employee.email}
                          employee={employee}
                          username={username}
                          handleDeleteTeamPopup={handleDeleteTeamPopup}
                          handleEditTeamPopup={handleEditTeamPopup}
                          popupData={popupData}
                          loggedInUserRole={loggedInUserRole}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {errors?.errorMessage != null &&
              errors?.errorType != null && <ApplicationDetailsErrorDialog />}
          </Grid2>
        </Grid2>
      </Container>

      <PopupDialog
        onClose={handleCloseDialog}
        openErrorPopup={openErrorPopup}
        headerMessage={headerMessage}
        componentName={componentName}
        handleAddEditTeam={handleAddEditTeam}
        errorMessage={errorMessage}
        popupData={popupData}
        handleDeleteTeam={handleDeleteTeam}
      />

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={snackMsg}
      />
    </>
  );
}

ApplicationTeamsInfo.propTypes = {
  teamsData: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    managers: PropTypes.array,
    members: PropTypes.array,
  }),
};

export default ApplicationTeamsInfo;
